import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GenderIcon from '../components/common/GenderIcon';
import TagList from '../components/Pet/TagList';
import LocalizedLink from '../components/common/LocalizedLink';
import PetService from '../services/petService';
import { useLanguage } from '../contexts/LanguageContext';

const { 
  FiUser, FiHeart, FiActivity, FiShield, FiPhone, FiMapPin, 
  FiAlertTriangle, FiCalendar, FiClock, FiMail, FiHome
} = FiIcons;

const SharePage = () => {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const { t, currentLanguage } = useLanguage();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const permissions = useMemo(() => {
    if (pet && pet.share_permissions && typeof pet.share_permissions === 'object') {
      console.log('🔒 SharePage: 使用資料庫中的權限設定:', pet.share_permissions);
      return pet.share_permissions;
    }

    const params = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    const permissionKeys = ['basicInfo', 'dietaryInfo', 'behaviorInfo', 'healthInfo', 'emergencyContact', 'specialNotes', 'photo'];
    const hasPermissionParams = permissionKeys.some(key => params.hasOwnProperty(key));

    if (!hasPermissionParams) {
      console.log('📋 SharePage: 無權限參數,預設全部開啟');
      return {
        basicInfo: true,
        dietaryInfo: true,
        behaviorInfo: true,
        healthInfo: true,
        emergencyContact: true,
        specialNotes: true,
        photo: true
      };
    }

    const result = {
      basicInfo: searchParams.get('basicInfo') === 'true',
      dietaryInfo: searchParams.get('dietaryInfo') === 'true',
      behaviorInfo: searchParams.get('behaviorInfo') === 'true',
      healthInfo: searchParams.get('healthInfo') === 'true',
      emergencyContact: searchParams.get('emergencyContact') === 'true',
      specialNotes: searchParams.get('specialNotes') === 'true',
      photo: searchParams.get('photo') === 'true'
    };

    console.log('🔒 SharePage: 根據 URL 參數設定權限:', result);
    return result;
  }, [pet, searchParams]);

  const petService = useMemo(() => new PetService(), []);

  useEffect(() => {
    if (pet && pet.name) {
      const metaTitle = t('sharePage.metaTitle', { petName: pet.name });
      const metaDescription = t('sharePage.metaDescription', { petName: pet.name });

      document.title = metaTitle;

      const updateMetaTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateMetaTag('og:title', metaTitle);
      updateMetaTag('og:description', metaDescription);
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('og:locale', currentLanguage === 'zh' ? 'zh_TW' : currentLanguage === 'ja' ? 'ja_JP' : 'en_US');
      
      if (pet.photo_url && permissions.photo) {
        updateMetaTag('og:image', pet.photo_url);
      }

      console.log(`✅ 已更新分享頁面 Meta Tags (${currentLanguage}):`, { metaTitle, metaDescription });
    }

    return () => {
      document.title = 'Pet Care Note';
    };
  }, [pet, currentLanguage, t, permissions.photo]);

  useEffect(() => {
    let mounted = true;

    const fetchSharedPet = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (!shareToken) {
          throw new Error("無效的分享連結");
        }

        const data = await petService.getPetByShareToken(shareToken);
        
        if (mounted) {
          if (data) {
            setPet(data);
          } else {
            setError(t('sharePage.linkExpiredOrIncorrect'));
          }
        }
      } catch (err) {
        console.error('獲取分享寵物資料錯誤:', err);
        if (mounted) {
          setError(t('sharePage.linkExpiredOrIncorrect'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchSharedPet();

    return () => {
      mounted = false;
    };
  }, [shareToken, t, petService]);

  const calculateAgeFromBirthDate = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    if (birth > today) return null;
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    if (months < 0) { years--; months += 12; }
    if (today.getDate() < birth.getDate()) { months--; if (months < 0) { years--; months += 12; } }
    return { years, months };
  };

  const formatAge = (pet) => {
    if (pet.birth_date) {
      const age = calculateAgeFromBirthDate(pet.birth_date);
      if (!age) return t('sharePage.unknown');
      if (age.years === 0) return age.months === 1 ? `1 ${t('sharePage.month')}` : `${age.months} ${t('sharePage.months')}`;
      else if (age.months === 0) return age.years === 1 ? `1 ${t('sharePage.year')}` : `${age.years} ${t('sharePage.years')}`;
      else return `${age.years} ${age.years === 1 ? t('sharePage.year') : t('sharePage.years')} ${age.months} ${age.months === 1 ? t('sharePage.month') : t('sharePage.months')}`;
    }
    if (pet.age) return `${pet.age} ${t('sharePage.years')}`;
    return t('sharePage.unknown');
  };

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  const InfoSection = ({ title, icon, children, className = "", colorScheme = "primary" }) => {
    const colorClasses = {
      primary: 'bg-indigo-100 text-indigo-600 border-indigo-200',
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      orange: 'bg-orange-100 text-orange-600 border-orange-200',
      red: 'bg-red-100 text-red-600 border-red-200'
    };
    return (
      <motion.div variants={itemVariants} className={`bg-white rounded-3xl shadow-lg p-8 border border-neutral-100 hover:shadow-xl transition-shadow duration-300 ${className}`}>
        <div className="flex items-center space-x-3 mb-6">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border-2 shadow-sm ${colorClasses[colorScheme]}`}>
            <SafeIcon icon={icon} className="text-xl" />
          </div>
          <h3 className="text-xl font-bold text-neutral-800">{title}</h3>
        </div>
        {children}
      </motion.div>
    );
  };

  const InfoItem = ({ label, value, icon, colorScheme = "gray", useTagList = false, tagColor = "indigo" }) => {
    if (!value) return null;
    const colorClasses = {
      gray: 'bg-gray-50 border-gray-200',
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      orange: 'bg-orange-50 border-orange-200',
      red: 'bg-red-50 border-red-200'
    };
    return (
      <div className={`p-4 rounded-2xl border shadow-sm hover:shadow-md transition-shadow duration-300 ${colorClasses[colorScheme]}`}>
        <div className="flex items-center mb-2">
          {icon && <SafeIcon icon={icon} className="mr-2 text-current opacity-70" />}
          <dt className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{label}</dt>
        </div>
        <dd className="text-neutral-800 leading-relaxed pl-6 font-medium">
          {useTagList ? (
            <TagList value={value} color={tagColor} emptyText={t('petProfile.notFilled')} />
          ) : (
            <span className="whitespace-pre-wrap break-words">{value}</span>
          )}
        </dd>
      </div>
    );
  };

  const getMealLabel = (time) => {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0], 10);
    if (isNaN(hour)) return '';
    
    if (hour >= 5 && hour < 11) return t('petProfile.breakfast');
    if (hour >= 11 && hour < 17) return t('petProfile.lunch');
    if (hour >= 17 && hour < 23) return t('petProfile.dinner');
    return t('petProfile.snack');
  };

  const renderFeedingPlan = (plan) => {
    if (!plan || !Array.isArray(plan) || plan.length === 0) return null;
    return (
      <div className="overflow-hidden rounded-2xl border border-red-100 shadow-sm">
        <div className="bg-red-50/50 px-4 py-2 border-b border-red-100 flex items-center">
          <SafeIcon icon={FiClock} className="text-red-400 mr-2 w-4 h-4" />
          <span className="text-sm font-semibold text-red-800">{t('petProfile.feedingSchedule')}</span>
        </div>
        <table className="min-w-full divide-y divide-red-100">
          <thead className="bg-red-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.time')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.portionSize')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-red-700 uppercase tracking-wider">{t('petProfile.foodType')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-red-50">
            {plan.sort((a,b) => a.time.localeCompare(b.time)).map((item, idx) => (
              <tr key={idx} className="hover:bg-red-50/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">
                  {item.time}
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">{getMealLabel(item.time)}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.portion || '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.food || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMedicationPlan = (plan) => {
    if (!plan || !Array.isArray(plan) || plan.length === 0) return null;
    
    const getFrequencyLabel = (freq) => {
      const map = {
        'Daily': t('petProfile.daily'),
        'BID': t('petProfile.twiceDaily'),
        'TID': t('petProfile.thriceDaily'),
        'Weekly': t('petProfile.weekly'),
        'Monthly': t('petProfile.monthly'),
        'Custom': t('petProfile.custom')
      };
      return map[freq] || freq;
    };

    return (
      <div className="overflow-hidden rounded-2xl border border-purple-100 shadow-sm">
        <div className="bg-purple-50/50 px-4 py-2 border-b border-purple-100 flex items-center">
          <SafeIcon icon={FiClock} className="text-purple-400 mr-2 w-4 h-4" />
          <span className="text-sm font-semibold text-purple-800">{t('petProfile.medicationSchedule')}</span>
        </div>
        <table className="min-w-full divide-y divide-purple-100">
          <thead className="bg-purple-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.medicationName')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.dosage')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.frequency')}</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">{t('petProfile.time')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-purple-50">
            {plan.map((item, idx) => (
              <tr key={idx} className="hover:bg-purple-50/30 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800">{item.name}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{item.dosage || '-'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{getFrequencyLabel(item.frequency)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {item.times && Array.isArray(item.times) ? (
                    <div className="flex flex-wrap gap-1">
                      {item.times.sort().map((t, i) => (
                        <span key={i} className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">{t}</span>
                      ))}
                    </div>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-gradient-to-br from-neutral-50 to-primary-50/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary-200 border-t-primary-600 mb-6 mx-auto"></div>
          <p className="text-neutral-700 text-lg font-medium">{t('sharePage.loadingPet')}</p>
        </div>
      </div>
    );
  }

  if (error || !pet) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex flex-col items-center justify-center text-center p-4 bg-gradient-to-br from-neutral-50 to-primary-50/30">
        <div className="w-20 h-20 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiAlertTriangle} className="text-3xl text-red-500" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-neutral-800">{t('sharePage.petInfoNotFound')}</h2>
        <p className="text-neutral-600 mb-8">{error}</p>
        <LocalizedLink to="/" className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition-colors shadow-lg">
          <SafeIcon icon={FiHome} className="mr-2" />
          {t('sharePage.backToHome')}
        </LocalizedLink>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="pt-20 sm:pt-24">
        <div className="bg-gradient-to-br from-neutral-50 to-primary-50/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              {/* Header */}
              <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-neutral-100 mt-8">
                <div className="md:flex">
                  {permissions.photo && (
                    <div className="md:w-2/5 relative min-h-[320px]">
                      {pet.photo_url ? (
                        <img 
                          src={pet.photo_url} 
                          alt={pet.name} 
                          className="absolute inset-0 w-full h-full object-cover" 
                          onError={(e) => { 
                            e.target.style.display = 'none'; 
                            e.target.nextSibling.style.display = 'flex'; 
                          }} 
                        />
                      ) : null}
                      <div className={`absolute inset-0 bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center ${pet.photo_url ? 'hidden' : 'flex'}`}>
                        <SafeIcon icon={FiUser} className="text-6xl text-neutral-400" />
                      </div>
                    </div>
                  )}
                  <div className={`${permissions.photo ? 'md:w-3/5' : 'w-full'} p-8`}>
                    <div className="mb-6">
                      <div className="flex items-center space-x-4 mb-3">
                        <h1 className="text-4xl font-bold text-neutral-800">{pet.name}</h1>
                        {permissions.basicInfo && <GenderIcon gender={pet.gender} petType={pet.type} size="xl" />}
                      </div>
                      {permissions.basicInfo && (
                        <div className="flex flex-wrap gap-4 text-lg text-neutral-600 mb-4">
                          <span className="flex items-center">
                            <SafeIcon icon={FiUser} className="mr-2" />
                            {pet.breed || t('petProfile.notSpecified')}
                          </span>
                          <span className="flex items-center">
                            <SafeIcon icon={FiCalendar} className="mr-2" />
                            {formatAge(pet)}
                          </span>
                          {pet.weight && (
                            <span className="flex items-center">
                              <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded mr-2">
                                {t('petProfile.weight')}
                              </span>
                              <TagList value={pet.weight} color="green" emptyText="-" />
                            </span>
                          )}
                        </div>
                      )}
                      {permissions.basicInfo && pet.current_location && (
                        <div className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                          <SafeIcon icon={FiMapPin} className="mr-1.5 w-4 h-4" />
                          {pet.current_location}
                        </div>
                      )}
                    </div>
                    {permissions.specialNotes && pet.special_notes && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6">
                        <h4 className="font-bold text-amber-800 mb-3 flex items-center">
                          <SafeIcon icon={FiHeart} className="mr-2" />
                          {t('petProfile.specialNotes')}
                        </h4>
                        <p className="text-amber-700 leading-relaxed">{pet.special_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Info */}
                {permissions.basicInfo && (
                  <InfoSection title={t('petProfile.basicInfo')} icon={FiUser} colorScheme="blue">
                    <div className="grid grid-cols-1 gap-4">
                      {pet.birth_date && (
                        <InfoItem 
                          label={t('petProfile.birthDate')} 
                          value={new Date(pet.birth_date).toLocaleDateString()} 
                          colorScheme="blue" 
                        />
                      )}
                      <InfoItem label={t('petProfile.microchipId')} value={pet.microchip_id} colorScheme="blue" />
                      <InfoItem label={t('petProfile.registrationId')} value={pet.registration_id} colorScheme="purple" />
                      <InfoItem label={t('petProfile.color')} value={pet.color} useTagList={true} tagColor="indigo" colorScheme="green" />
                      <InfoItem label={t('petProfile.height')} value={pet.height} useTagList={true} tagColor="orange" colorScheme="orange" />
                      <InfoItem label={t('petProfile.coatType')} value={pet.coat_type} useTagList={true} tagColor="purple" colorScheme="purple" />
                      <InfoItem label={t('petProfile.eyeColor')} value={pet.eye_color} useTagList={true} tagColor="teal" colorScheme="blue" />
                      <InfoItem label={t('petProfile.bodySize')} value={pet.body_size} useTagList={true} tagColor="blue" colorScheme="blue" />
                      <InfoItem label={t('petProfile.specialMarkings')} value={pet.special_markings} useTagList={true} tagColor="pink" colorScheme="purple" />
                      <InfoItem label={t('petProfile.currentLocation')} value={pet.current_location} icon={FiMapPin} colorScheme="red" />
                    </div>
                  </InfoSection>
                )}

                {/* Dietary Info */}
                {permissions.dietaryInfo && (
                  <InfoSection title={t('petProfile.dietaryInfo')} icon={FiHeart} colorScheme="red">
                    <div className="flex flex-col gap-6">
                      {pet.feeding_plan && pet.feeding_plan.length > 0 ? (
                        renderFeedingPlan(pet.feeding_plan)
                      ) : (
                        <InfoItem label={t('petProfile.feedingSchedule')} value={pet.feeding_schedule} colorScheme="blue" />
                      )}
                      <InfoItem label={t('petProfile.canEat')} value={pet.can_eat} useTagList={true} tagColor="green" colorScheme="green" />
                      <InfoItem label={t('petProfile.cannotEat')} value={pet.cannot_eat} useTagList={true} tagColor="red" colorScheme="red" />
                    </div>
                  </InfoSection>
                )}

                {/* Behavior Info */}
                {permissions.behaviorInfo && (
                  <InfoSection title={t('petProfile.behaviorInfo')} icon={FiActivity} colorScheme="green">
                    <div className="flex flex-col gap-6">
                      <InfoItem label={t('petProfile.temperament')} value={pet.temperament} useTagList={true} tagColor="yellow" colorScheme="purple" />
                      <InfoItem label={t('petProfile.likes')} value={pet.likes} useTagList={true} tagColor="pink" colorScheme="green" />
                      <InfoItem label={t('petProfile.dislikes')} value={pet.dislikes} useTagList={true} tagColor="orange" colorScheme="orange" />
                      <InfoItem label={t('petProfile.exerciseNeeds')} value={pet.exercise_needs} useTagList={true} tagColor="teal" colorScheme="blue" />
                      <InfoItem label={t('petProfile.trainingNotes')} value={pet.training_notes} colorScheme="purple" />
                    </div>
                  </InfoSection>
                )}

                {/* Health Info */}
                {permissions.healthInfo && (
                  <InfoSection title={t('petProfile.healthInfo')} icon={FiShield} colorScheme="purple">
                    <div className="flex flex-col gap-6">
                      {pet.medication_plan && pet.medication_plan.length > 0 ? (
                        renderMedicationPlan(pet.medication_plan)
                      ) : (
                        <>
                          <InfoItem label={t('petProfile.medications')} value={pet.medications} colorScheme="blue" />
                          <InfoItem label={t('petProfile.medicationSchedule')} value={pet.medication_schedule} colorScheme="green" />
                        </>
                      )}
                      <InfoItem label={t('petProfile.vaccines')} value={pet.vaccines} useTagList={true} tagColor="blue" colorScheme="green" />
                      <InfoItem label={t('petProfile.nextVaccineDate')} value={pet.next_vaccine_date} colorScheme="orange" />
                      <InfoItem label={t('petProfile.allergies')} value={pet.allergies} useTagList={true} tagColor="orange" colorScheme="red" />
                      <InfoItem label={t('petProfile.medicalConditions')} value={pet.medical_conditions} useTagList={true} tagColor="red" colorScheme="red" />
                    </div>
                  </InfoSection>
                )}

                {/* Vet Info */}
                {permissions.emergencyContact && (
                  <InfoSection 
                    title={t('petProfile.veterinarianInfo')} 
                    icon={FiPhone} 
                    className="lg:col-span-2" 
                    colorScheme="orange"
                  >
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pet.vet_name && (
                          <InfoItem label={t('petProfile.vetName')} value={pet.vet_name} useTagList={true} tagColor="orange" icon={FiUser} colorScheme="orange" />
                        )}
                        {pet.vet_phone && (
                          <InfoItem label={t('petProfile.vetPhone')} value={pet.vet_phone} useTagList={true} tagColor="red" icon={FiPhone} colorScheme="red" />
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pet.vet_address && (
                          <InfoItem label={t('petProfile.vetAddress')} value={pet.vet_address} useTagList={true} tagColor="blue" icon={FiMapPin} colorScheme="blue" />
                        )}
                        {pet.vet_email && (
                          <InfoItem label={t('petProfile.vetEmail')} value={pet.vet_email} useTagList={true} tagColor="purple" icon={FiMail} colorScheme="purple" />
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pet.insurance_provider && (
                          <InfoItem label={t('petProfile.insuranceProvider')} value={pet.insurance_provider} useTagList={true} tagColor="blue" colorScheme="blue" />
                        )}
                        {pet.insurance_policy && (
                          <InfoItem label={t('petProfile.insurancePolicy')} value={pet.insurance_policy} useTagList={true} tagColor="purple" colorScheme="purple" />
                        )}
                      </div>
                    </div>
                  </InfoSection>
                )}
              </div>

              {/* Footer Message */}
              <motion.div variants={itemVariants} className="mt-12 text-center">
                <div className="inline-block bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 max-w-2xl">
                  <SafeIcon icon={FiShield} className="text-blue-600 text-3xl mb-3 mx-auto" />
                  <h3 className="text-lg font-bold text-blue-900 mb-2">{t('sharePage.securityReminder')}</h3>
                  <p className="text-blue-700 text-sm">{t('sharePage.shareOnlyWithTrusted')}</p>
                  <p className="text-blue-400 text-xs mt-4">© {new Date().getFullYear()} Pet Care Note</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePage;